import { array, fn, hash } from '@ember/helper';
import { action } from '@ember/object';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import queue from '@nullvoxpopuli/ember-composable-helpers/helpers/queue';
import { focusTrap } from 'ember-focus-trap';
import { t, type IntlService } from 'ember-intl';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import BvBadge from 'volta/components/bv-badge';
import BvBtn from 'volta/components/bv-btn';
import BvPopover from 'volta/components/bv-popover';
import BvTag from 'volta/components/bv-tag';
import FiltersBaseFilter from 'volta/components/filters/base-filter';
import { indexByFn } from 'volta/utils/array-utils';
import { deepEqual } from 'volta/utils/object-utils';

import type { IResourceName } from 'volta/models/base-model';
import type LabelService from 'volta/services/label-service';

interface BvFiltersFilterTagsSignature {
  Args: {
    disabled?: boolean;
    resourceName: IResourceName;
    filterDefinitions: TFilterDefinition[];
    filterValues: TFilterValues;
    onFilterValuesChange: (value: TFilterValue, field: string) => void;
  };
  Element: HTMLElement;
  Blocks: {
    default: [];
  };
}

export default class BvFiltersFilterTagsComponent extends Component<BvFiltersFilterTagsSignature> {
  @service labelService!: LabelService;
  @service intl!: IntlService;

  @tracked disabledSearchBtn: boolean = true;
  @tracked filterValues: TFilterValues = this.args.filterValues;
  @tracked initialFilterValues?: TFilterValues = undefined;

  @cached
  get filters(): (TFilterValue & { field: string })[] {
    return (Object.entries(this.filterValues) as [string, TFilterValue][]).map(
      ([field, f]: [string, TFilterValue]) => {
        return {
          ...f,
          field
        };
      }
    );
  }

  @cached
  get definitionsDict() {
    return indexByFn(this.args.filterDefinitions, (d) => d.queryParam);
  }

  @action
  onChange(filterValue: TFilterValue, field: string) {
    this.filterValues = { ...this.filterValues, [field]: filterValue };
    this.disabledSearchBtn = false;
  }

  @action
  handleInitialValuesChange(_element: HTMLElement, [initialFilterValues]: [TFilterValues]) {
    this.handleSetupFilterValues(initialFilterValues);
  }

  @action
  handleSetupFilterValues(filterValues: TFilterValues) {
    this.initialFilterValues = filterValues;
    if (!deepEqual(this.filterValues, filterValues)) {
      this.filterValues = filterValues;
    }
  }

  @action
  handleSubmit(field: string) {
    if (this.filterValues[field]) {
      this.initialFilterValues = this.filterValues;
      this.args.onFilterValuesChange(this.filterValues[field], field);
    }
    this.disabledSearchBtn = true;
  }

  @action
  rollbackUnvalidatedChanges() {
    this.filterValues = this.initialFilterValues!;
    this.disabledSearchBtn = true;
  }

  getDef = (key: string): TFilterDefinition | undefined => this.definitionsDict[key];

  getFilterValue = (key?: string) => (key ? this.filterValues[key] : undefined);

  getTitle = (
    filter: TFilterValue & {
      field: string;
    },
    title?: string
  ) => {
    if (filter.isCustomField) {
      return this.labelService.tOrElse(filter.field, title);
    } else {
      return (
        title ??
        this.labelService.tOrElse(
          `filterDefinitions.${filter.field}`,
          this.labelService.intl.t(filter.field)
        )
      );
    }
  };

  formatFilterValue = (
    filter: TFilterValue & {
      field: string;
    }
  ) => {
    if (filter.comparator === 'eq') {
      const [value] = filter.values;
      return value === 'true'
        ? this.intl.t('yes')
        : value === 'false'
          ? this.intl.t('no')
          : String(filter.values.length ?? 0);
    } else {
      return String(filter.values.length ?? 0);
    }
  };

  <template>
    {{#let this.disabledSearchBtn this.filters as |disabledSearchBtn filters|}}
      {{! template-lint-disable no-at-ember-render-modifiers }}
      <div
        class='bv-tags'
        {{didInsert (fn this.handleSetupFilterValues @filterValues)}}
        {{didUpdate this.handleInitialValuesChange @filterValues}}
        ...attributes
      >
        {{#each filters key='field' as |filter|}}
          {{#let (this.getDef filter.field) as |definition|}}
            {{#if (and definition (not definition.hidden))}}
              {{#let (this.getTitle filter definition.title) as |title|}}
                <BvPopover @size='md' @onClose={{this.rollbackUnvalidatedChanges}} as |popover|>
                  <BvTag
                    class='u-animation-appear-left'
                    @disabled={{@disabled}}
                    @onRemove={{unless
                      definition.required
                      (fn
                        @onFilterValuesChange
                        (hash enabled=filter.enabled comparator=filter.comparator values=(array))
                        filter.field
                      )
                    }}
                    @onClick={{popover.toggle}}
                    @color='primary'
                    {{popover.trigger}}
                  >
                    {{title}}
                    <BvBadge
                      @color='teal'
                      @text={{this.formatFilterValue filter}}
                      @size='sm'
                      @outline={{true}}
                      class='u-ml-1'
                    />
                  </BvTag>
                  <popover.Content
                    {{focusTrap isActive=popover.isOpen focusTrapOptions=popover.focusTrapOptions}}
                  >
                    <popover.Body>
                      <FiltersBaseFilter
                        @filterValues={{this.filterValues}}
                        {{! @glint-expect-error: here definition is defined }}
                        @definition={{definition}}
                        @resourceName={{@resourceName}}
                        @initialValue={{this.getFilterValue definition.queryParam}}
                        @onChange={{this.onChange}}
                        @clearDefaultValue={{definition.clearDefaultValue}}
                      />
                    </popover.Body>
                    <popover.Footer>
                      <BvBtn @onClick={{popover.close}} @size='sm' @text={{t 'cancel'}} />
                      <BvBtn
                        @color='primary'
                        {{! @glint-ignore }}
                        @onClick={{queue (fn this.handleSubmit filter.field) popover.close}}
                        @disabled={{disabledSearchBtn}}
                        @size='sm'
                        @text={{t 'search'}}
                      />
                    </popover.Footer>
                  </popover.Content>
                </BvPopover>
              {{/let}}
            {{/if}}
          {{/let}}
        {{/each}}
        {{yield}}
      </div>
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'BvFilters::FilterTags': typeof BvFiltersFilterTagsComponent;
    'bv-filters/filter-tags': typeof BvFiltersFilterTagsComponent;
  }
}
